import { useRouteLocale } from "@/hooks/useRouteLocale";
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "@/hooks/useTranslation";
import { usePageMeta } from "@/hooks/usePageMeta";
import { AdminHeader } from "@/components/admin/AdminHeader";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  getCampaigns,
  updateCampaign,
  deleteCampaign,
  createCampaign,
  type Campaign,
} from "@/services/api/campaigns";
import { ADMIN_ROUTES } from "@/routes/admin";
import { toast } from "sonner";
import {
  Plus,
  Loader2,
  Tag,
  Clock,
  CheckCircle2,
  XCircle,
  FileQuestion,
  Link2,
  KeyRound,
  Pencil,
  Trash2,
  BarChart3,
} from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

export default function CampaignManagement() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const _locale = useRouteLocale();
  const currentLocale = _locale || "ro";
  usePageMeta({
    title: t("admin.campaigns.metaTitle"),
    description: t("admin.campaigns.description"),
    locale: currentLocale,
    path: ADMIN_ROUTES.campaigns(currentLocale),
    noindex: true,
  });

  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState<string | null>(null);
  const [typeFilter, setTypeFilter] = useState<string>("all");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [deleteDialog, setDeleteDialog] = useState<{ open: boolean; campaign: Campaign | null }>({
    open: false,
    campaign: null,
  });
  const [createDialog, setCreateDialog] = useState(false);
  const [newCampaign, setNewCampaign] = useState({
    name: "",
    type: "hidden" as "form" | "access" | "hidden",
  });
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    loadCampaigns();
  }, [typeFilter, statusFilter]);

  const loadCampaigns = async () => {
    try {
      setLoading(true);
      const data = await getCampaigns({
        type: typeFilter !== "all" ? (typeFilter as "form" | "access" | "hidden") : undefined,
        status: statusFilter !== "all" ? (statusFilter as "enabled" | "disabled" | "active") : undefined,
      });
      setCampaigns(data.items);
    } catch (error: unknown) {
      console.error("Failed to load campaigns:", error);
      toast.error(t("common.error"));
    } finally {
      setLoading(false);
    }
  };

  const handleToggleEnabled = async (campaign: Campaign) => {
    if (!campaign.canBeEnabled && !campaign.enabled) {
      toast.error(t("admin.campaigns.cannotEnableWithoutDates"));
      return;
    }

    try {
      setUpdating(campaign.uuid);
      await updateCampaign(campaign.uuid, { enabled: !campaign.enabled });
      await loadCampaigns();
      toast.success(
        campaign.enabled
          ? t("admin.campaigns.disabled")
          : t("admin.campaigns.enabled")
      );
    } catch (error: unknown) {
      console.error("Failed to update campaign:", error);
      const errorMessage = (error as { response?: { data?: { error?: { message?: string } } } })?.response?.data?.error?.message;
      toast.error(errorMessage || t("common.error"));
    } finally {
      setUpdating(null);
    }
  };

  const handleDelete = async () => {
    if (!deleteDialog.campaign) return;

    try {
      setUpdating(deleteDialog.campaign.uuid);
      await deleteCampaign(deleteDialog.campaign.uuid);
      await loadCampaigns();
      toast.success(t("admin.campaigns.deleted"));
      setDeleteDialog({ open: false, campaign: null });
    } catch (error: unknown) {
      console.error("Failed to delete campaign:", error);
      toast.error(t("common.error"));
    } finally {
      setUpdating(null);
    }
  };

  const handleCreate = async () => {
    if (!newCampaign.name.trim()) {
      toast.error(t("admin.campaigns.nameRequired"));
      return;
    }

    try {
      setCreating(true);
      const campaign = await createCampaign({
        name: newCampaign.name,
        type: newCampaign.type,
      });
      toast.success(t("admin.campaigns.created"));
      setCreateDialog(false);
      setNewCampaign({ name: "", type: "hidden" });
      // Navigate to editor
      navigate(ADMIN_ROUTES.campaignEditor(currentLocale, campaign.uuid));
    } catch (error: unknown) {
      console.error("Failed to create campaign:", error);
      toast.error(t("common.error"));
    } finally {
      setCreating(false);
    }
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case "form":
        return <FileQuestion className="h-4 w-4" />;
      case "access":
        return <Link2 className="h-4 w-4" />;
      case "hidden":
        return <KeyRound className="h-4 w-4" />;
      default:
        return <Tag className="h-4 w-4" />;
    }
  };

  const getTypeLabel = (type: string) => {
    switch (type) {
      case "form":
        return t("admin.campaigns.typeForm");
      case "access":
        return t("admin.campaigns.typeAccess");
      case "hidden":
        return t("admin.campaigns.typeHidden");
      default:
        return type;
    }
  };

  const getStatusBadge = (campaign: Campaign) => {
    if (!campaign.enabled) {
      return (
        <Badge variant="secondary">
          <XCircle className="h-3 w-3 mr-1" />
          {t("admin.campaigns.statusDisabled")}
        </Badge>
      );
    }

    if (campaign.isActive) {
      return (
        <Badge variant="default" className="bg-green-500">
          <CheckCircle2 className="h-3 w-3 mr-1" />
          {t("admin.campaigns.statusActive")}
        </Badge>
      );
    }

    const now = new Date();
    const startAt = campaign.startAt ? new Date(campaign.startAt) : null;
    const endAt = campaign.endAt ? new Date(campaign.endAt) : null;

    if (startAt && now < startAt) {
      return (
        <Badge variant="outline" className="border-blue-500 text-blue-500">
          <Clock className="h-3 w-3 mr-1" />
          {t("admin.campaigns.statusScheduled")}
        </Badge>
      );
    }

    if (endAt && now > endAt) {
      return (
        <Badge variant="outline" className="border-gray-500 text-gray-500">
          <Clock className="h-3 w-3 mr-1" />
          {t("admin.campaigns.statusExpired")}
        </Badge>
      );
    }

    return (
      <Badge variant="secondary">
        <XCircle className="h-3 w-3 mr-1" />
        {t("admin.campaigns.statusDisabled")}
      </Badge>
    );
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return "-";
    return new Date(dateString).toLocaleDateString(currentLocale === "ro" ? "ro-RO" : "en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <AdminHeader currentLocale={currentLocale} active="campaigns" />
        <div className="container mx-auto px-4 py-12">
          <div className="flex items-center justify-center h-64">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <AdminHeader currentLocale={currentLocale} active="campaigns" />
      <div className="container mx-auto px-4 py-12">
        <div className="flex items-start justify-between gap-4 mb-8">
          <div>
            <h1 className="text-3xl font-bold mb-2">
              {t("admin.campaigns.title")}
            </h1>
            <p className="text-muted-foreground">
              {t("admin.campaigns.description")}
            </p>
          </div>
          <Button onClick={() => setCreateDialog(true)} className="shrink-0">
            <Plus className="h-4 w-4 mr-2" />
            {t("admin.campaigns.create")}
          </Button>
        </div>

        {/* Filters */}
        <div className="flex gap-4 mb-6">
          <div className="w-48">
            <Label className="text-sm text-muted-foreground mb-1 block">
              {t("admin.campaigns.filterType")}
            </Label>
            <Select value={typeFilter} onValueChange={setTypeFilter}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t("common.all")}</SelectItem>
                <SelectItem value="form">{t("admin.campaigns.typeForm")}</SelectItem>
                <SelectItem value="access">{t("admin.campaigns.typeAccess")}</SelectItem>
                <SelectItem value="hidden">{t("admin.campaigns.typeHidden")}</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="w-48">
            <Label className="text-sm text-muted-foreground mb-1 block">
              {t("admin.campaigns.filterStatus")}
            </Label>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t("common.all")}</SelectItem>
                <SelectItem value="enabled">{t("admin.campaigns.statusEnabled")}</SelectItem>
                <SelectItem value="disabled">{t("admin.campaigns.statusDisabled")}</SelectItem>
                <SelectItem value="active">{t("admin.campaigns.statusActive")}</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Campaign List */}
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {campaigns.map((campaign) => {
            const isUpdating = updating === campaign.uuid;

            return (
              <Card key={campaign.uuid} className="relative">
                <CardHeader>
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        {getTypeIcon(campaign.type)}
                        <CardTitle className="text-lg">{campaign.name}</CardTitle>
                      </div>
                      <CardDescription className="flex items-center gap-2">
                        <Badge variant="outline" className="text-xs">
                          {getTypeLabel(campaign.type)}
                        </Badge>
                        {campaign.codesCount !== undefined && (
                          <span className="text-xs text-muted-foreground">
                            {campaign.codesCount} {t("admin.campaigns.codes")}
                          </span>
                        )}
                      </CardDescription>
                    </div>
                    {getStatusBadge(campaign)}
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {/* Date Range */}
                    <div className="text-sm text-muted-foreground">
                      <div className="flex justify-between">
                        <span>{t("admin.campaigns.startAt")}:</span>
                        <span>{formatDate(campaign.startAt)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span>{t("admin.campaigns.endAt")}:</span>
                        <span>{formatDate(campaign.endAt)}</span>
                      </div>
                    </div>

                    {/* Enable Toggle */}
                    <div className="flex items-center justify-between">
                      <Label htmlFor={`toggle-${campaign.uuid}`} className="text-sm">
                        {t("admin.campaigns.enable")}
                      </Label>
                      <Switch
                        id={`toggle-${campaign.uuid}`}
                        checked={campaign.enabled}
                        onCheckedChange={() => handleToggleEnabled(campaign)}
                        disabled={isUpdating}
                      />
                    </div>

                    {/* Actions */}
                    <div className="flex gap-2 pt-2 border-t">
                      <Button
                        variant="outline"
                        size="sm"
                        className="flex-1"
                        onClick={() => navigate(ADMIN_ROUTES.campaignEditor(currentLocale, campaign.uuid))}
                      >
                        <Pencil className="h-4 w-4 mr-2" />
                        {t("common.edit")}
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => navigate(`${ADMIN_ROUTES.campaignEditor(currentLocale, campaign.uuid)}?tab=stats`)}
                      >
                        <BarChart3 className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="destructive"
                        size="sm"
                        onClick={() => setDeleteDialog({ open: true, campaign })}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {campaigns.length === 0 && (
          <Card>
            <CardContent className="py-12 text-center">
              <Tag className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
              <p className="text-muted-foreground mb-4">
                {t("admin.campaigns.noCampaigns")}
              </p>
              <Button onClick={() => setCreateDialog(true)}>
                <Plus className="h-4 w-4 mr-2" />
                {t("admin.campaigns.create")}
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Delete Dialog */}
        <AlertDialog open={deleteDialog.open} onOpenChange={(open) => setDeleteDialog({ open, campaign: null })}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>
                {t("admin.campaigns.deleteConfirm")}
              </AlertDialogTitle>
              <AlertDialogDescription>
                {t("admin.campaigns.deleteWarning")}
                {deleteDialog.campaign && (
                  <span className="block mt-2 font-semibold">{deleteDialog.campaign.name}</span>
                )}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>
                {t("common.cancel")}
              </AlertDialogCancel>
              <AlertDialogAction
                onClick={handleDelete}
                disabled={updating !== null}
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              >
                {updating !== null ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    {t("common.deleting")}
                  </>
                ) : (
                  t("common.delete")
                )}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Create Dialog */}
        <Dialog open={createDialog} onOpenChange={setCreateDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{t("admin.campaigns.createTitle")}</DialogTitle>
              <DialogDescription>
                {t("admin.campaigns.createDescription")}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="name">{t("admin.campaigns.name")}</Label>
                <Input
                  id="name"
                  value={newCampaign.name}
                  onChange={(e) => setNewCampaign({ ...newCampaign, name: e.target.value })}
                  placeholder={t("admin.campaigns.namePlaceholder")}
                />
              </div>
              <div className="space-y-2">
                <Label>{t("admin.campaigns.type")}</Label>
                <Select
                  value={newCampaign.type}
                  onValueChange={(v) => setNewCampaign({ ...newCampaign, type: v as "form" | "access" | "hidden" })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="form" textValue={t("admin.campaigns.typeForm")}>
                      <div className="flex items-center gap-2">
                        <FileQuestion className="h-4 w-4" />
                        {t("admin.campaigns.typeForm")}
                      </div>
                    </SelectItem>
                    <SelectItem value="access" textValue={t("admin.campaigns.typeAccess")}>
                      <div className="flex items-center gap-2">
                        <Link2 className="h-4 w-4" />
                        {t("admin.campaigns.typeAccess")}
                      </div>
                    </SelectItem>
                    <SelectItem value="hidden" textValue={t("admin.campaigns.typeHidden")}>
                      <div className="flex items-center gap-2">
                        <KeyRound className="h-4 w-4" />
                        {t("admin.campaigns.typeHidden")}
                      </div>
                    </SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  {newCampaign.type === "form" && t("admin.campaigns.typeFormDescription")}
                  {newCampaign.type === "access" && t("admin.campaigns.typeAccessDescription")}
                  {newCampaign.type === "hidden" && t("admin.campaigns.typeHiddenDescription")}
                </p>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setCreateDialog(false)}>
                {t("common.cancel")}
              </Button>
              <Button onClick={handleCreate} disabled={creating}>
                {creating ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    {t("common.creating")}
                  </>
                ) : (
                  t("common.create")
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}
